import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DcrType, EntityType } from '@/core/graphql/generated/types';

import { HcoDcrSectionFields } from '../_constants/HcoDcrSectionsFields';
import { HcpDcrSectionFields } from '../_constants/HcpDcrSectionsFields';
import { useDcrFields } from '../_constants/useDcrFields';
import type { HcoDcrValuesIn } from '../_schemas/hcoSchema';
import type { HcpDcrValuesIn } from '../_schemas/hcpSchema';
import type { DcrAttachment } from '../_types/attachment';
import { getDirtyValues } from '../_utils/getFormDirtyValues';
import { AttachmentList } from '../Attachments/AttachmentList/AttachmentList';

import { reviewModalContentStyles as sx } from './ReviewModalContent.styles';
import { ReviewSection } from './ReviewSection/ReviewSection';

type Props = {
	entityType: EntityType;
	dcrType: DcrType;
};

export const ReviewModalContent = ({ entityType, dcrType }: Props) => {
	const { t } = useTranslation();
	const { allFields } = useDcrFields();

	const {
		control,
		formState: { dirtyFields, defaultValues }
	} = useFormContext<HcpDcrValuesIn | HcoDcrValuesIn>();

	const sectionFields =
		entityType === EntityType.Hcp ? HcpDcrSectionFields : HcoDcrSectionFields;

	const currentValues = useWatch({ control });
	const attachments = (useWatch({ control, name: 'attachments' }) ??
		[]) as DcrAttachment[];

	const changedValues = useMemo(
		() => getDirtyValues(dirtyFields, currentValues, defaultValues),
		[dirtyFields, currentValues, defaultValues]
	);

	const hasChanges = Object.keys(changedValues).length > 0;

	if (!hasChanges && attachments.length === 0) {
		return (
			<Typography sx={sx.noChangesText}>{t('No changes detected.')}</Typography>
		);
	}

	return (
		<Box sx={sx.root}>
			{Object.entries(sectionFields).map(([sectionName, fields]) => (
				<ReviewSection
					key={sectionName}
					sectionName={sectionName}
					fields={fields}
					changedValues={changedValues}
					defaultValues={defaultValues}
					currentValues={currentValues}
					fieldTranslations={allFields}
					dcrType={dcrType}
				/>
			))}

			{attachments.length > 0 && (
				<Box sx={sx.attachmentsSection}>
					<Typography sx={sx.sectionTitle}>{t('Attachments')}</Typography>
					<Divider />
					<AttachmentList attachments={attachments} />
				</Box>
			)}
		</Box>
	);
};
